/**
 * Contenido REAL de Atlas para los tres estilos de interfaz (ADR-2026-07-30-tres-estilos-interfaz-shells-propios).
 *
 * Las tres pantallas de `/estilos/*` son shells visuales distintos sobre el MISMO
 * contenido: los modos de generación (`MODES`), los ejemplos por modo
 * (`MODE_EXAMPLES`), los resultados por rubro del alta (`GENERATION_RESULTS`) y las
 * secciones que hoy lista el Sidebar. Nada de esto se duplica acá: se importa de su
 * fuente y sólo se le agrega lo que el shell necesita para pintarlo (ícono, tono,
 * nombre corto).
 *
 * Regla: si un dato ya existe en la app, se importa. Lo único inventado acá son
 * etiquetas de navegación que en el original de referencia no tienen equivalente
 * exacto en Atlas (p.ej. "Explorar"), y quedan marcadas como tal.
 */
import { MODES } from '../v2/modesCatalog';
import { MODE_EXAMPLES } from '../v2/modeExamples';
import { templatePreviewUrl } from '../v2/templatesApi';
import { GENERATION_RESULTS, SETUP_CATEGORIES } from '../../components/onboarding/accountSetup';
import { FLUJOS_ENABLED } from '../flujos/flags';
import { MODO_MODELO } from '../../lib/nombreDeModelo';

// ── Saludo ────────────────────────────────────────────────────────────────────
// Mismo criterio horario que la home (`Welcome.tsx` / `InterfazV2`): día hasta las
// 15, tarde hasta las 20, noche después. Se calcula en hora de Argentina para que
// no dependa del reloj de la máquina que esté mirando el preview.
const AR_TZ = 'America/Argentina/Buenos_Aires';

export function saludo(): string {
  let hora: number;
  try {
    hora = parseInt(
      new Intl.DateTimeFormat('es-AR', { hour: 'numeric', hour12: false, timeZone: AR_TZ }).format(
        new Date(),
      ),
      10,
    );
  } catch {
    hora = new Date().getHours();
  }
  if (hora >= 6 && hora < 15) return 'Buenos días';
  if (hora >= 15 && hora < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

// ── Herramientas (modos de generación) ────────────────────────────────────────

export interface HerramientaAtlas {
  /** `path` del modo en `MODES`, o un id propio para las que no son modos V2. */
  id: string;
  /** Nombre corto — para chips, filas de íconos y grillas densas. */
  nombre: string;
  /** Etiqueta de menú: verbal y de una línea, para listas de sidebar. */
  etiquetaMenu: string;
  /** Título largo, tal cual lo muestra hoy la app. */
  titulo: string;
  descripcion: string;
  /** Material Symbol (la familia ya está cargada en `index.html`). */
  icono: string;
  /** Ruta REAL de la app: los clicks del preview llevan al flujo de verdad. */
  ruta: string;
  poster: string;
  video?: string;
  /** Clases del cuadrado de color en las filas de herramientas (estilo Workspace). */
  tono: string;
  /**
   * Los flags de visibilidad de `ModeCard`, COPIADOS del catálogo —
   * ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares,
   * ADR-2026-09-09-la-sidebar-filtra-por-los-flags-del-catalogo.
   *
   * `HERRAMIENTAS` se arma con un `.map()` sobre `MODES` que se quedaba sólo con lo
   * presentacional, así que la sidebar —el único consumidor que lista ESTA estructura
   * en vez de `MODES`— no tenía con qué filtrar. Resultado: era la única de las cuatro
   * superficies que no chequeaba `devOnly`, y un modo interno salía de `/modos` y del
   * sheet pero seguía listado en "Todas las herramientas"; y el gating por rubro corría
   * contra una lista de ids escrita a mano, que quedaba vieja cada vez que se sumaba un
   * modo de rubro (pasó con los tres nuevos de Hogar y Decoración).
   *
   * `undefined` en las de `HERRAMIENTAS_EXTRA` (Plantillas, Estudio de Video), que no
   * salen de `MODES` y no tienen estos ejes.
   */
  devOnly?: boolean;
  ocultoEnListas?: boolean;
  soloHogarDeco?: boolean;
  ocultoEnHogarDeco?: boolean;
  /**
   * Qué produce la herramienta — ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen.
   *
   * Es el eje con el que la sidebar y `/modos` agrupan. Sale de
   * `preset.outputType`, que es la MISMA regla que ya usaba `useModosGenerar` para el
   * sheet de Generar de mobile: si acá se escribiera una lista de ids aparte, un modo
   * nuevo podría quedar en "Video" en el sheet y en "Imagen" en la sidebar.
   */
  familia: 'video' | 'imagen';
  soloBelleza?: boolean;
  soloModa?: boolean;
}

/** Metadata de presentación por modo — lo único que `MODES` no trae. */
const PRESENTACION_MODOS: Record<
  string,
  { nombre: string; etiquetaMenu: string; icono: string; tono: string }
> = {
  auto: {
    nombre: 'Automático',
    etiquetaMenu: 'Generar imágenes',
    icono: 'auto_awesome',
    tono: 'bg-[#EEF1FE] text-[#1644EB]',
  },
  // El modo se llama "Modelo" en todas las marcas, no sigue al rubro
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo). Estos dos campos son el nombre
  // del modo para los shells que no leen `COPY_MODOS`, así que dicen lo mismo que la
  // card de la home: nunca "Con avatar".
  modelo: {
    nombre: `Con ${MODO_MODELO.singular}`,
    etiquetaMenu: `Imagen con ${MODO_MODELO.singular}`,
    icono: 'person',
    tono: 'bg-[#F1EDFD] text-[#6D3BEB]',
  },
  catalogo: {
    nombre: 'Catálogo',
    etiquetaMenu: 'Catálogo con IA',
    icono: 'grid_view',
    tono: 'bg-[#E8F6EF] text-[#0E9F6E]',
  },
  carrusel: {
    nombre: 'Carrusel',
    etiquetaMenu: 'Carrusel de imágenes',
    icono: 'burst_mode',
    tono: 'bg-[#EAF3FE] text-[#1D6FD1]',
  },
  /**
   * Los dos modos del rubro Hogar y Decoración —
   * ADR-2026-08-05-muebles-e-infografia-tienen-icono-propio.
   *
   * Faltaban acá, así que caían al fallback de abajo y los DOS se dibujaban con
   * `auto_awesome`: en la sidebar quedaban dos ítems seguidos con el mismo ícono, que es
   * justo donde el ícono es lo único que distingue a simple vista.
   *
   * Catálogo de muebles lleva el MISMO `grid_view` que Catálogo IA a propósito: se llama
   * igual (ADR-2026-08-05-el-catalogo-de-muebles-se-llama-catalogo) y nunca se ven juntos
   * —uno es `soloHogarDeco` y el otro `ocultoEnHogarDeco`—, así que es el mismo modo
   * contado para otro rubro y tiene que verse igual.
   *
   * Infografía va con `straighten`, la regla: el modo son fichas con las MEDIDAS del
   * producto, y es el mismo glifo que ya usa la card "Medidas y características" adentro
   * del modo.
   */
  // El Catálogo con el agente (ADR-2026-10-13-catalogo-con-el-agente): mismo lugar y glifo
  // que tenía el de muebles, al que reemplazó (ADR-2026-10-14-catalogo-reemplaza-a-catalogo-muebles).
  'catalogo-agente': {
    nombre: 'Catálogo',
    etiquetaMenu: 'Catálogo',
    icono: 'grid_view',
    tono: 'bg-[#E8F6EF] text-[#0E9F6E]',
  },
  infografia: {
    nombre: 'Infografía',
    etiquetaMenu: 'Infografías de producto',
    icono: 'straighten',
    tono: 'bg-[#EEF1FE] text-[#1644EB]',
  },
  'video-influencer': {
    // "Video con influencer", el mismo nombre que `COPY_TIRA` y que el `title` del
    // preset — ADR-2026-08-25-el-modo-video-se-llama-video-con-influencer. Este
    // `nombre` es el respaldo de `etiquetaSidebar` y lo que muestran los shells de
    // preview (`EstiloGaleria`, `EstiloWorkspace`); con "Video" eran una quinta
    // tabla que decía otra cosa.
    nombre: 'Video con influencer',
    etiquetaMenu: 'Generar videos',
    icono: 'movie',
    tono: 'bg-[#FDEDF3] text-[#D6336C]',
  },
  asistente: {
    nombre: 'Crear desde cero',
    etiquetaMenu: 'Crear desde cero',
    icono: 'forum',
    tono: 'bg-[#FEF3E2] text-[#C2740B]',
  },
  // El primero de los tres modos que parten al asistente
  // (ADR-2026-09-07-modo-prompt-libre): un prompt y listo, así que el glifo es el de
  // escribir y no el de conversar.
  'prompt-libre': {
    nombre: 'Prompt libre',
    etiquetaMenu: 'Prompt libre',
    icono: 'edit_note',
    tono: 'bg-[#F3E8FF] text-[#7C3AED]',
  },
  // El segundo de la partición (ADR-2026-09-07-modo-creador-video). `videocam` y no
  // `movie`, que ya es el de Video con influencer.
  'creador-video': {
    nombre: 'Creador de video',
    etiquetaMenu: 'Creador de video',
    icono: 'videocam',
    tono: 'bg-[#EAF3FF] text-[#0B5ED7]',
  },
  anuncio: {
    nombre: 'Anuncio',
    etiquetaMenu: 'Anuncios',
    icono: 'campaign',
    tono: 'bg-[#E6F6FB] text-[#0E7490]',
  },
  /**
   * Los tres que quedaban cayendo al fallback `auto_awesome` —
   * ADR-2026-09-14-tres-modos-mas-tienen-icono-propio, que es el mismo problema y el
   * mismo arreglo que ADR-2026-08-05-muebles-e-infografia-tienen-icono-propio.
   *
   * En la sidebar de Moda/Belleza quedaban CUATRO ítems con la misma chispa (éstos tres
   * más "Automática"), que es justo donde el ícono es lo único que distingue a simple
   * vista. La chispa se la queda "Automática": es el modo cuyo argumento ES que la IA
   * arma la escena sola.
   *
   * `nombre` y `etiquetaMenu` repiten el `preset.title` —exactamente lo que daba el
   * fallback— para que esto cambie el ícono y NADA más: el texto de la sidebar sale de
   * `COPY_MODOS` vía `nombreCortoModo`, pero otros shells leen estos dos campos. El
   * `tono` neutro también es el del fallback; darles color es otra decisión.
   */
  'animar-imagen': {
    nombre: 'Animar imagen',
    etiquetaMenu: 'Animar imagen',
    // `animation` y no `movie` (Video con influencer) ni `videocam` (Creador de video):
    // los tres terminan en un video, pero éste es el único que parte de una foto quieta
    // y le pone movimiento, que es lo que el glifo dibuja.
    icono: 'animation',
    tono: 'bg-neutral-100 text-neutral-700',
  },
  'fotos-producto': {
    nombre: 'Fotos de producto',
    etiquetaMenu: 'Fotos de producto',
    // La sesión de fotos del producto solo. `grid_view` ya es Catálogo —el otro modo de
    // producto sin modelo— y ahí la diferencia es que Catálogo son varias vistas de una
    // tanda; éste es sacarle fotos.
    icono: 'photo_camera',
    tono: 'bg-neutral-100 text-neutral-700',
  },
  'replicar-foto': {
    nombre: 'Recrear imagen',
    etiquetaMenu: 'Recrear imagen',
    // Replicar es copiar: traés una foto que te gustó y sale la misma con tu producto.
    // `burst_mode` (Carrusel) es el otro candidato obvio y ya está tomado — y además
    // dice "varias", que es lo contrario de este modo.
    icono: 'content_copy',
    tono: 'bg-neutral-100 text-neutral-700',
  },
  'diseno-indumentaria': {
    nombre: 'Diseño de indumentaria',
    etiquetaMenu: 'Diseño de indumentaria',
    icono: 'checkroom',
    tono: 'bg-[#FDF1E8] text-[#C2410C]',
  },
};

/** Los 6 modos de generación, con su preset real y su ruta real en V2. */
export const HERRAMIENTAS: HerramientaAtlas[] = MODES.map((modo) => {
  const pres = PRESENTACION_MODOS[modo.path] ?? {
    nombre: modo.preset.title,
    etiquetaMenu: modo.preset.title,
    icono: 'auto_awesome',
    tono: 'bg-neutral-100 text-neutral-700',
  };
  return {
    id: modo.path,
    nombre: pres.nombre,
    etiquetaMenu: pres.etiquetaMenu,
    titulo: modo.preset.title,
    descripcion: modo.preset.description ?? '',
    icono: pres.icono,
    ruta: modo.route ?? `/interfaz-v2/modo/${modo.path}`,
    poster: modo.poster,
    video: modo.video,
    tono: pres.tono,
    // Los flags van tal cual del catálogo: acá NO se decide nada, sólo se transportan
    // para que la sidebar pueda filtrar con la misma regla que el resto.
    devOnly: modo.devOnly,
    ocultoEnListas: modo.ocultoEnListas,
    soloHogarDeco: modo.soloHogarDeco,
    ocultoEnHogarDeco: modo.ocultoEnHogarDeco,
    // Misma regla que `useModosGenerar`: la familia sale del `outputType` del preset
    // y no de una lista escrita a mano.
    familia: modo.preset.outputType === 'video' ? 'video' : 'imagen',
    soloBelleza: modo.soloBelleza,
    soloModa: modo.soloModa,
  };
});

/** Herramientas que no son modos V2 pero comparten fila en la app (Modos, Sidebar). */
export const HERRAMIENTAS_EXTRA: HerramientaAtlas[] = [
  {
    id: 'plantillas',
    nombre: 'Plantillas',
    etiquetaMenu: 'Plantillas',
    titulo: 'Plantillas',
    descripcion: 'Arrancá de una plantilla ya armada y cambiale el producto.',
    icono: 'dashboard_customize',
    ruta: '/interfaz-v2/plantillas',
    // El collage de escenas, que es lo que las Plantillas SON —
    // ADR-2026-08-20-plantillas-dejan-de-usar-la-caratula-de-anuncio.
    //
    // Acá apuntaba a `modo_anuncio/modo_anuncio_hero.png`: el hero de OTRO modo, un
    // collage de tres avisos de zapatilla con copy publicitario y marca de terceros a
    // la vista. En el drawer de "¿Qué querés generar?" la card de Plantillas mostraba
    // un aviso, que es justo lo que hace Anuncio.
    //
    // Es además el MISMO asset que ya usa el sheet de Generar de mobile
    // (`CARATULA_PLANTILLAS` en `useModosGenerar`), así que las dos superficies dejan
    // de mostrar cosas distintas para la misma opción.
    poster: '/images/modo_plantillas/caratula.jpg',
    tono: 'bg-[#EDF0F5] text-[#3F3F46]',
    // Las dos de acá no salen de `MODES`, así que no hay `outputType` del que
    // deducirla: va escrita. Plantillas arma imágenes.
    familia: 'imagen',
  },
  {
    // El modo Plantillas de Belleza y Cuidado Personal
    // (ADR-2026-09-10-plantillas-belleza-modo-propio). Entrada propia y no un `if`
    // sobre la de arriba: son dos modos, con rutas y pantallas distintas, y quien
    // filtra la lista (sidebar, `/modos`, los sheets) decide por rubro cuál mostrar.
    // Nunca se ven los dos juntos: los rubros son excluyentes.
    id: 'plantillas-belleza',
    nombre: 'Plantillas',
    etiquetaMenu: 'Plantillas',
    titulo: 'Plantillas',
    descripcion: 'Arrancá de una plantilla ya armada y cambiale el producto.',
    icono: 'dashboard_customize',
    ruta: '/interfaz-v2/plantillas-belleza',
    // La misma carátula que Plantillas de Moda por ahora: la copia arranca idéntica
    // y lo primero que se va a cambiar son las plantillas, no la portada.
    poster: '/images/modo_plantillas/caratula.jpg',
    tono: 'bg-[#EDF0F5] text-[#3F3F46]',
    // Igual que Plantillas de Moda: arma imágenes.
    familia: 'imagen',
  },
  {
    id: 'editor-video',
    nombre: 'Estudio de Video',
    etiquetaMenu: 'Estudio de Video',
    titulo: 'Estudio de Video',
    descripcion: 'Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.',
    icono: 'movie_edit',
    ruta: '/editor-video',
    poster: '/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg',
    tono: 'bg-[#EAEAF6] text-[#4C3BEB]',
    familia: 'video',
  },
  {
    id: 'flujos',
    nombre: 'Flujos',
    etiquetaMenu: 'Flujos',
    titulo: 'Flujos',
    descripcion: 'Encadená nodos: producto → prompt → imagen → voz → video, y volvé a correrlo con otro producto.',
    icono: 'account_tree',
    ruta: '/flujos',
    // Carátula EN VIDEO — ADR-2026-09-22-la-caratula-de-flujos-pasa-a-video. El `poster`
    // es el CUADRO 0 del clip ya comprimido, así lo quieto y lo que arranca son lo mismo.
    // Las superficies que no reproducen video (la sidebar, los sheets) se quedan con el
    // poster y siguen mostrando la misma pieza.
    poster: '/images/modo_flujos/caratula.jpg',
    video: '/images/modo_flujos/caratula.mp4',
    tono: 'bg-[#E8EEFF] text-[#1644EB]',
    // Sólo el equipo mientras el flag esté apagado (ADR-2026-09-04-canvas-flujos-enfoque).
    // La sidebar filtra por `devOnly` con `modoVisiblePorFlags`, igual que a los modos.
    // Con el flag prendido (ADR-2026-09-14-flujos-es-de-pro-y-ultra) el ítem lo ve todo el
    // mundo y el piso Pro/Ultra lo pone `getModePlanLock('flujos')`: la sidebar cae en el
    // `?? h.id` igual que con Estudio de Video y dibuja el candado.
    devOnly: !FLUJOS_ENABLED,
    // El canvas encadena hasta el video y en la sidebar va con Estudio de Video, como
    // en la lista plana de antes (ADR-2026-09-15-merge-onboarding-usuarios-pagos-a-principal).
    familia: 'video',
  },
];

export const TODAS_LAS_HERRAMIENTAS = [...HERRAMIENTAS, ...HERRAMIENTAS_EXTRA];

/* Acá vivía `CARRUSEL_MODOS`: la lista de modos con sus carátulas, que además PISABA
   la del catálogo para Catálogo (su grilla de vistas en vez del hero). Era la tabla que
   armaba la tira de la home, y con el tiempo pasó a ser de dónde sacaban la carátula el
   sheet de Generar, el drawer de Mi marca y el hero de mobile — pero no `/modos`, que
   leía `MODES` directo y por eso mostraba otra imagen para el mismo modo.

   Se fue en ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo: la pisada vive
   ahora en `CARATULA_PROPIA` (`modesCatalog`) y la escalera entera —portada del rubro →
   pisada → catálogo → preset— la resuelve `caratulaModo`, que es lo que llaman todas las
   pantallas. */

// ── Navegación ────────────────────────────────────────────────────────────────

export interface ItemNav {
  label: string;
  icono: string;
  ruta: string;
  /** Sólo decorativo dentro del preview (no existe como pantalla propia hoy). */
  soloVisual?: boolean;
  insignia?: string;
}

/** Secciones de generación — las mismas que hoy lista `FLAT_MENU_ITEMS` del Sidebar. */
export const NAV_CREAR: ItemNav[] = [
  { label: 'Inicio', icono: 'home', ruta: '/interfaz-v2' },
  { label: 'Estudio virtual', icono: 'auto_awesome', ruta: '/interfaz-v2' },
  { label: 'Modos', icono: 'view_agenda', ruta: '/modos' },
  { label: 'Plantillas', icono: 'dashboard_customize', ruta: '/interfaz-v2/plantillas' },
  { label: 'Historial', icono: 'schedule', ruta: '/redes-sociales/imagenes' },
  { label: 'Estudio de Video', icono: 'movie_edit', ruta: '/editor-video' },
];

/** Marca y catálogo — lo que en el Sidebar cae bajo "Mi marca" / Inventario. */
export const NAV_MARCA: ItemNav[] = [
  { label: 'Mi marca', icono: 'shopping_bag', ruta: '/mi-marca' },
  { label: 'Productos', icono: 'inventory_2', ruta: '/productos' },
  { label: 'Stock', icono: 'inventory', ruta: '/stock' },
  { label: 'Descuentos', icono: 'sell', ruta: '/descuentos' },
];

/** Cuenta / soporte — pie del Sidebar actual. */
export const NAV_CUENTA: ItemNav[] = [
  { label: 'Planes', icono: 'workspace_premium', ruta: '/subscription' },
  { label: 'Facturación', icono: 'receipt_long', ruta: '/billing' },
  { label: 'Configuración', icono: 'settings', ruta: '/configuracion' },
  { label: 'Ayuda', icono: 'help', ruta: '/ayuda' },
];

// ── Galerías (imágenes reales de la app) ──────────────────────────────────────

export interface ItemGaleria {
  src: string;
  alt: string;
  /** MP4 cuando el resultado es un video (se marca con un play encima). */
  video?: string;
  /** Nombre a superponer sobre la imagen (lo usan las plantillas). */
  titulo?: string;
  /** Agrupador que se muestra junto al título (categoría de la plantilla). */
  subtitulo?: string;
}

export interface FilaGaleria {
  id: string;
  titulo: string;
  subtitulo: string;
  items: ItemGaleria[];
}

/** Imágenes de un set de `GENERATION_RESULTS` (resultados reales del alta). */
function desdeResultados(...ids: string[]): ItemGaleria[] {
  return ids.flatMap((id) =>
    (GENERATION_RESULTS[id] ?? []).map((r) => ({
      src: r.src,
      alt: `${r.label} — ${id.replace(/_/g, ' ')}`,
      video: r.video,
    })),
  );
}

/** Imágenes de `MODE_EXAMPLES` (ejemplos reales por modo). */
function desdeEjemplos(kind: string): ItemGaleria[] {
  return (MODE_EXAMPLES[kind] ?? []).flatMap((ej) => {
    if (ej.type === 'image') return [{ src: ej.url, alt: `Ejemplo de ${kind}` }];
    if (ej.type === 'video')
      return [{ src: ej.poster ?? '', alt: `Ejemplo de ${kind}`, video: ej.url }];
    return [];
  });
}

/**
 * Filas de contenido del estilo Galería. Cada fila es una categoría real del
 * cuestionario de alta (`SETUP_CATEGORIES`) o un modo, con sus imágenes reales.
 */
export const FILAS_GALERIA: FilaGaleria[] = [
  {
    id: 'moda',
    titulo: 'Moda e indumentaria',
    subtitulo: 'Fotos de catálogo, tomas para redes y fondo blanco listo para publicar',
    items: [...desdeResultados('moda_cartera', 'moda_camisa'), ...desdeEjemplos('design-modelo')],
  },
  {
    id: 'hogar',
    titulo: 'Hogar y decoración',
    subtitulo: 'Producto en escena, con la luz y el ambiente resueltos por la IA',
    items: desdeResultados('hogar_mesa_luz', 'hogar_silla'),
  },
  {
    id: 'gastronomia',
    titulo: 'Gastronomía',
    subtitulo: 'Tomas apetitosas de tus platos, sin sesión de fotos',
    items: [...desdeResultados('gastro_hamburguesa', 'gastro_pizza')],
  },
  {
    id: 'belleza',
    titulo: 'Belleza y cuidado personal',
    subtitulo: 'Packaging y detalle de producto con terminación de marca',
    items: desdeResultados('belleza_mascara', 'belleza_lapiz'),
  },
  {
    id: 'catalogo',
    titulo: 'Catálogo con IA',
    subtitulo: 'El mismo producto en varias vistas y poses, en un solo proceso',
    items: [...desdeEjemplos('catalogo-ia'), ...desdeEjemplos('video-influencer')],
  },
  {
    id: 'anuncios',
    titulo: 'Anuncios y contenido para redes',
    subtitulo: 'Piezas listas para publicar, con texto, formato y escena resueltos',
    items: [...desdeEjemplos('anuncio'), ...desdeEjemplos('design-auto')],
  },
];

/** Chips de categoría del estilo Galería — los rubros reales del alta. */
export const CHIPS_CATEGORIAS = SETUP_CATEGORIES.filter((c) => c.id !== 'otros').map((c) => ({
  id: c.id,
  label: c.label,
}));

/** Grilla plana de creaciones recientes (para los estilos que muestran historial). */
export const CREACIONES_RECIENTES: ItemGaleria[] = FILAS_GALERIA.flatMap((f) => f.items).slice(0, 12);

/**
 * Plantillas REALES de la plataforma — una por categoría del catálogo global.
 *
 * Nombre, categoría y `preview_opt_path` salen de la tabla `templates` de producción
 * (globales, activas, de tipo imagen). Las imágenes se sirven del bucket `templates`,
 * que es **público**: se ven sin sesión, que es la condición de estas rutas.
 *
 * ⚠️ Es un SNAPSHOT tomado a mano. No se pueden traer en vivo desde acá: con la key
 * anónima y sin sesión, RLS devuelve 0 filas (verificado contra la REST API), así que
 * un `fetch` dejaría la sección vacía. Si este estilo se adopta —ya dentro de la app,
 * con sesión— esta constante se reemplaza por `fetchTemplates()`
 * (`pages/v2/templatesApi`), que devuelve exactamente estos campos.
 */
// Las 10 categorías globales que tienen 6 o más plantillas de imagen, con sus
// primeras 6 por `orden`. Nombres de categoría y de plantilla, TAL CUAL están en la
// base (incluidas las que van sin tilde, p. ej. "Mediterranea"). La bajada sí es
// propia: la `descripcion` de la tabla es un párrafo de prompt, no copy de UI.
const PLANTILLAS_REALES: { categoria: string; bajada: string; items: [string, string][] }[] = [
  {
    categoria: 'Alfombra roja',
    bajada: 'Flashes, prensa y pose de estreno',
    items: [
      ['Entrando', 'globales/bd729fe4-d4d1-4cc4-b1ed-dd64a2500602_opt_1779393582303.webp'],
      ['Posando', 'globales/b0d6c0b8-532e-4d4d-b9cb-313b52726e0e_opt_1779423712740.webp'],
      ['Subiendo escaleras', 'globales/a8d1aa9f-1dfd-4536-8752-a5df11d0788d_opt_1779749188318.webp'],
      ['Vista en altura', 'globales/b14d72dc-4235-4bbe-8e54-31c128276708_opt_1779425360564.webp'],
      ['En foco', 'globales/d964cf59-0d3b-4646-a1a8-2562e72ffc66_opt_1779423778924.webp'],
      ['Paparatzi', 'globales/e0bbbd82-c4f6-4efb-90cb-e526ed2478ab_opt_1779398975250.webp'],
    ],
  },
  {
    categoria: 'Editorial',
    bajada: 'Alta moda de tapa: pose fuerte y luz dramática',
    items: [
      ['Banco de parque', 'globales/608e2487-c0e5-4806-932e-4c0b57c10d56_opt_1781993847918.webp'],
      ['Silla cromada', 'globales/5b623b09-d463-4769-9416-e5fc1bf5980b_opt_1781993112168.webp'],
      ['Escalera urbana', 'globales/edb5f2e5-a9ff-4620-a3fe-224af2ad26c4_opt_1781993133707.webp'],
      ['Mano al rostro', 'globales/802d01b1-4275-4bba-9db5-1bb59e7da2e4_opt_1781993151162.webp'],
      ['Jardin de villa', 'globales/49349687-405c-4d42-99c4-be60d8479a2c_opt_1781993164256.webp'],
      ['Rascacielos', 'globales/7cdd5d40-a5fa-40f6-b819-9ef6c2728e63_opt_1781993192165.webp'],
    ],
  },
  {
    categoria: 'Fiebre Mundialista',
    bajada: 'Bandera, estadio y aguante',
    items: [
      ['Bandera en el estadio', 'globales/46418d45-63b5-47c4-b5c8-080d137a6fa6_opt_1782949491571.webp'],
      ['Aliento celeste', 'globales/0df9afe1-5e1b-4d7b-beaa-00de61e86783_opt_1782950176100.webp'],
      ['Bandera desplegada', 'globales/9709a2d3-aa66-4837-9d5c-d9562db031ff_opt_1782950186604.webp'],
      ['Pintura de hincha', 'globales/78bbd078-baee-4dc4-a436-be73b064f761_opt_1782950633069.webp'],
      ['Festejo en la calle', 'globales/b998cc3d-f15b-4149-b429-565a7cf27eb6_opt_1782950650242.webp'],
      ['Bandera y gorra', 'globales/a8fc3966-6b79-4e74-be5b-03e46287d33e_opt_1782951914065.webp'],
    ],
  },
  {
    categoria: 'Indie',
    bajada: 'Luz natural, color cálido y aire de película',
    items: [
      ['Auto Vintage', 'globales/f06be958-9ddb-4cbe-adc1-585da9e0be98_opt_1781042123849.webp'],
      ['Pradera de margaritas', 'globales/50457836-54eb-4db6-8d84-ab9c46a786c5_opt_1781042142563.webp'],
      ['Sillón azul', 'globales/2ce358e1-8607-41eb-8bde-65f5fb23db61_opt_1781042161232.webp'],
      ['Recostada en el sofá', 'globales/784600b3-dc4a-4b83-84a1-d63fa90ba92f_opt_1781042188561.webp'],
      ['Pared de piedra', 'globales/30600fdc-6070-40f8-9c5b-cd7286345e16_opt_1781042208769.webp'],
      ['Campo con caballo', 'globales/bcbf03eb-a0d3-4e94-ae55-3f5e4a9c9c5c_opt_1781042230022.webp'],
    ],
  },
  {
    categoria: 'Maximalismo',
    bajada: 'Color saturado, estampas y terciopelo',
    items: [
      ['Living retro lila', 'globales/99fc507c-a79c-4db0-8089-925fac75340e_opt_1782759821234.webp'],
      ['Escalera de neón', 'globales/91a64cfe-47f4-4f18-ad7c-da68cbd65d24_opt_1782759793922.webp'],
      ['Telón rojo dorado', 'globales/3a5f78ff-d123-450d-9988-b9b85f71b5f2_opt_1782759835670.webp'],
      ['Salón estampado', 'globales/7277ad44-ddba-411a-9950-41e15e801071_opt_1782759859662.webp'],
      ['Sofá de terciopelo', 'globales/4e145314-3ff7-4040-b440-841bb698bcc9_opt_1782759879573.webp'],
      ['Diván damasco', 'globales/27db9bac-dfb1-495a-9445-751eca012391_opt_1782760017881.webp'],
    ],
  },
  {
    categoria: 'Mediterranea',
    bajada: 'Sol, cal y mar del sur de Europa',
    items: [
      ['Velero al atardecer', 'globales/8a468911-ab5e-4c7b-9ee0-07517d5b01d1_opt_1781992491500.webp'],
      ['Rocas al mar', 'globales/af10cc0d-1987-4748-b057-08c6728b3ef4_opt_1781992517578.webp'],
      ['Marea baja', 'globales/e156807f-47d0-4779-b883-a9a0b30dbb1e_opt_1781996014230.webp'],
      ['Persianas azules', 'globales/ea91bc8d-88c8-4237-bc9e-4ed42a93b29f_opt_1781992561857.webp'],
      ['Club de playa', 'globales/191db3c4-a333-4848-92d3-14a047934b95_opt_1781992579914.webp'],
      ['Catamaran', 'globales/b114b87a-1b21-43c5-98c3-61eebc7ef1da_opt_1782186296926.webp'],
    ],
  },
  {
    categoria: 'Minimalista',
    bajada: 'Fondo limpio, paleta neutra, foco en la prenda',
    items: [
      ['Estudio neutro', 'globales/d9af77ed-b123-44fb-bca2-62bf7e0760b5_opt_1781991702762.webp'],
      ['Sofá de cuero', 'globales/6a35a65f-e76d-4403-a016-0c5ea7b05d09_opt_1781991740720.webp'],
      ['Sombra de árbol', 'globales/cff24b8b-9b1d-4708-9de9-4d40a52b4be9_opt_1781991767436.webp'],
      ['Loft con ventanal', 'globales/c6484f52-db23-4b91-b8a9-b68764b210c6_opt_1781991864175.webp'],
      ['Aparador retro', 'globales/1045b488-127d-46e2-8071-5a6bf07f2418_opt_1781991880810.webp'],
      ['Plano abierto', 'globales/005e5586-7373-42eb-90fc-21b1bcbc62f7_opt_1781991893854.webp'],
    ],
  },
  {
    categoria: 'Old Money',
    bajada: 'Lujo discreto, sastrería y club de campo',
    items: [
      ['Yate en B&N', 'globales/32a010dd-ba44-435d-8bc0-d289bdaf33e3_opt_1781042628600.webp'],
      ['Campo de flores', 'globales/ac605ff3-5d4f-4b12-af53-c4dcd82c9417_opt_1781042644591.webp'],
      ['Tarde de polo', 'globales/d3060d5f-d3ae-4c76-93ff-47cd7560bdc4_opt_1781042660867.webp'],
      ['Fuente de mármol', 'globales/dc029e01-f3d4-41b8-910b-d2ed48ad871e_opt_1781042677318.webp'],
      ['Equitación', 'globales/b50809e5-04fb-4b2f-ab8e-5858399ac3cd_opt_1781042711331.webp'],
      ['Tenis', 'globales/364c1a3e-2941-47e8-8f36-8132c6f5ce61_opt_1781042752488.webp'],
    ],
  },
  {
    categoria: 'Running',
    bajada: 'Zancada, pista y energía deportiva',
    items: [
      ['Pista atardecer', 'globales/9bf66bdb-1b21-4abb-a671-001f997fe109_opt_1782187299403.webp'],
      ['Zancada dorada', 'globales/6eabf4ed-833f-4c91-a426-d671d3edd583_opt_1782234061670.webp'],
      ['Salto al cielo', 'globales/a3d231c7-8132-4f89-b9a4-cee65bb41837_opt_1782234087013.webp'],
      ['Sneaker en mano', 'globales/fc101b71-1322-4c1f-91ea-96d0ef4d366b_opt_1782236851994.webp'],
      ['Zapatilla flotando', 'globales/c63687d5-c3b0-4f41-8478-a714adbbd810_opt_1782237191608.webp'],
      ['Salto entre torres', 'globales/d5742607-a7f4-4edd-b510-19783c59f064_opt_1782237703723.webp'],
    ],
  },
  {
    categoria: 'Streetwear',
    bajada: 'Ciudad, asfalto y luz cruda',
    items: [
      ['Cruce urbano', 'globales/d3796319-72bf-4a39-9b66-59942b36da85_opt_1781990625763.webp'],
      ['Escalones', 'globales/ab8cbf3f-f437-47a5-a03d-65dfe6060119_opt_1781990671882.webp'],
      ['Subte', 'globales/fef08ded-bb0c-4fe9-a967-a33e22c5b11d_opt_1781990697839.webp'],
      ['Street prep', 'globales/1bf3e6a5-7288-4487-a05b-9999cf56d39d_opt_1781990715523.webp'],
      ['Estación de servicio', 'globales/44bf3298-f241-466a-8597-7751ef954e36_opt_1781990736528.webp'],
      ['Cordón', 'globales/b736610c-e32a-4293-8957-6f183f5148b8_opt_1781990774082.webp'],
    ],
  },
];

export interface FilaPlantillas {
  categoria: string;
  bajada: string;
  items: ItemGaleria[];
}

/**
 * Todas las categorías con sus seis ejemplos, listas para pintar. La URL la arma
 * `templatePreviewUrl`, el helper canónico de la app.
 */
export const FILAS_PLANTILLAS: FilaPlantillas[] = PLANTILLAS_REALES.map((cat) => ({
  categoria: cat.categoria,
  bajada: cat.bajada,
  items: cat.items.map(([nombre, path]) => ({
    src: templatePreviewUrl(path),
    alt: `Plantilla ${nombre} — ${cat.categoria}`,
    titulo: nombre,
  })),
}));

/** Categorías mostradas en la visita anterior, para no repetirlas. */
const CLAVE_ROTACION = 'atlas:estilos-plantillas-vistas';

/**
 * Lo mostrado la visita anterior, leído UNA sola vez al cargar el módulo.
 *
 * No se relee dentro de `elegirFilasPlantillas`: en dev, `StrictMode` invoca el
 * `useMemo` dos veces, y releyendo, la segunda pasada excluía lo que acababa de
 * guardar la primera —no lo de la visita anterior— con lo que las categorías de la
 * visita previa podían volver a salir. Congelado acá, las dos pasadas excluyen lo
 * mismo y la que se muestra (la última) siempre es distinta de la anterior.
 */
const PREVIAS: string[] = (() => {
  try {
    const crudo = localStorage.getItem(CLAVE_ROTACION);
    const parsed = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
})();

/**
 * Elige `cantidad` categorías al azar **excluyendo las de la visita anterior**, y
 * deja registrado lo elegido para la próxima. Así cada entrada o refresh de la home
 * muestra un corte distinto del catálogo (ADR-2026-07-30-plantillas-minimal-10-categorias-rotando).
 *
 * Con 10 categorías y 3 por visita siempre quedan 7 candidatas, así que la exclusión
 * nunca deja la sección corta. El `slice` de seguridad está igual por si el catálogo
 * se achica.
 */
export function elegirFilasPlantillas(cantidad = 3): FilaPlantillas[] {
  const candidatas = FILAS_PLANTILLAS.filter((f) => !PREVIAS.includes(f.categoria));
  const pozo = candidatas.length >= cantidad ? candidatas : FILAS_PLANTILLAS;

  // Fisher-Yates sobre una copia: no se toca el orden del catálogo.
  const mezclado = [...pozo];
  for (let i = mezclado.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [mezclado[i], mezclado[j]] = [mezclado[j], mezclado[i]];
  }
  const elegidas = mezclado.slice(0, cantidad);

  try {
    localStorage.setItem(CLAVE_ROTACION, JSON.stringify(elegidas.map((f) => f.categoria)));
  } catch {
    /* sin storage, la próxima visita elige sin exclusión */
  }
  return elegidas;
}

/**
 * El símbolo (Material) con el que la sidebar y la home dibujan una herramienta, por su id:
 * el de su presentación si es un modo, o el de `HERRAMIENTAS_EXTRA` (Estudio de Video,
 * Flujos). Lo usa la lista de Herramientas del teléfono para mostrar los MISMOS íconos
 * (ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono). `undefined` si no tiene.
 */
export function iconoDeHerramienta(id: string): string | undefined {
  return PRESENTACION_MODOS[id]?.icono ?? HERRAMIENTAS_EXTRA.find((h) => h.id === id)?.icono;
}
